import { useState, useEffect } from 'react';
import { useParty } from '../hooks/useParty';
import { useLanguage } from '../hooks/useLanguage';

// MUI components
import {
  Box,
  Typography,
  Paper,
  Button,
  Tooltip,
  Snackbar,
  IconButton,
  Alert
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';

const PartyCodeDisplay = ({ partyId, partyCode }) => {
  const { getPartyCode } = useParty();
  const { t } = useLanguage();
  
  const [code, setCode] = useState(partyCode || '');
  const [loading, setLoading] = useState(!partyCode && !!partyId);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    // If partyCode is directly provided, use it
    if (partyCode) {
      setCode(partyCode);
      setLoading(false);
      return;
    }
    
    // Otherwise fetch it using partyId
    const fetchCode = async () => {
      try {
        const fetchedPartyCode = await getPartyCode(partyId);
        setCode(fetchedPartyCode);
        setLoading(false);
      } catch (err) {
        setError(t.failedToLoadPartyCode);
        setLoading(false);
      }
    };
    
    if (partyId) {
      fetchCode();
    }
  }, [partyId, partyCode, getPartyCode, t]);
  
  const handleCopyCode = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        })
        .catch(err => {
          console.error('Failed to copy code:', err);
          // Fallback method
          fallbackCopyTextToClipboard(code);
        });
    } else {
      // Fallback for browsers that don't support clipboard API
      fallbackCopyTextToClipboard(code);
    }
  };
  
  // Fallback copy method for older browsers
  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    
    // Make the textarea out of viewport
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } else {
        console.error('Fallback: Unable to copy');
      }
    } catch (err) {
      console.error('Fallback: Unable to copy', err);
    }
    
    document.body.removeChild(textArea);
  };
  
  // For the icon button version (when used in the header)
  if (partyCode) {
    return (
      <>
        <Tooltip title={t.clickToCopyPartyCode}>
          <IconButton 
            color="inherit" 
            onClick={handleCopyCode}
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            <CopyIcon />
          </IconButton>
        </Tooltip>
        
        <Snackbar
          open={copied}
          autoHideDuration={3000}
          onClose={() => setCopied(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" variant="filled">
            {t.partyCodeCopied}
          </Alert>
        </Snackbar>
      </>
    );
  }
  
  if (loading) {
    return <Typography>{t.loadingPartyCode}</Typography>;
  }
  
  if (error) {
    return <Typography color="error">{error}</Typography>;
  }
  
  // Full paper version (when used standalone)
  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        {t.sharePartyCode}
      </Typography>
      
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          my: 2
        }}
      >
        <Typography 
          variant="h4" 
          component="div" 
          sx={{ 
            fontFamily: 'monospace',
            letterSpacing: 1,
            fontWeight: 'bold',
            bgcolor: 'background.default',
            px: 2,
            py: 1,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider'
          }}
        >
          {code}
        </Typography>
      </Box>
      
      <Button
        variant="contained"
        color="primary"
        startIcon={<CopyIcon />}
        onClick={handleCopyCode}
        fullWidth
      >
        {t.copyPartyCode}
      </Button>
      
      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled">
          {t.partyCodeCopied}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PartyCodeDisplay; 